/**
 * ============================================================
 * MyVelvetTouch First-Party Analytics Tracker
 * Version: 0.1.1
 * Security Update: October 9, 2026
 * ============================================================
 *
 * Security Improvements:
 *
 * 1. Disable production debug logging
 * 2. Remove URL query strings and fragments
 * 3. Sanitize document.referrer
 * 4. Preserve HTTPS transmission
 * 5. Maintain existing Cloud Run payload compatibility
 *
 * Tracking:
 *
 * - Persistent anonymous visitor ID
 * - Session ID
 * - Automatic page_view
 * - Cloud Run event collection
 *
 * IMPORTANT:
 *
 * This version does not implement:
 * - Consent management
 * - Server-side event validation
 * - Rate limiting
 * - Event property allowlisting
 *
 * These require further integration and review.
 * ============================================================
 */

(() => {
  "use strict";

  /* ============================================================
     01. CONFIGURATION
  ============================================================ */

  const CONFIG = {
    endpoint:
      "https://mvt-event-collector-380146042275.us-central1.run.app",

    anonymousStorageKey: "mvt_anonymous_id",

    sessionStorageKey: "mvt_session_id",

    // Disable debug logging in production.
    debug: false
  };

  /* ============================================================
     02. DEBUG LOGGING
  ============================================================ */

  function debugLog(...args) {
    if (CONFIG.debug) {
      console.log("[MVT Tracker]", ...args);
    }
  }

  /* ============================================================
     03. UUID GENERATOR
  ============================================================ */

  function createUUID() {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID === "function"
    ) {
      return window.crypto.randomUUID();
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;

        const v =
          c === "x"
            ? r
            : (r & 0x3) | 0x8;

        return v.toString(16);
      }
    );
  }

  /* ============================================================
     04. ANONYMOUS VISITOR ID
  ============================================================ */

  /**
   * Persistent anonymous visitor ID.
   *
   * Stored in localStorage.
   *
   * The same browser can be recognized
   * across multiple sessions.
   */

  function getAnonymousId() {
    let anonymousId = null;

    try {
      anonymousId = localStorage.getItem(
        CONFIG.anonymousStorageKey
      );

      if (!anonymousId) {
        anonymousId = createUUID();

        localStorage.setItem(
          CONFIG.anonymousStorageKey,
          anonymousId
        );

        debugLog(
          "New anonymous_id created:",
          anonymousId
        );
      }
    } catch (error) {
      anonymousId = createUUID();

      debugLog(
        "localStorage unavailable. Temporary anonymous_id:",
        anonymousId
      );
    }

    return anonymousId;
  }

  /* ============================================================
     05. SESSION ID
  ============================================================ */

  /**
   * Session ID.
   *
   * Stored in sessionStorage.
   *
   * Same tab:
   * - Reuses session_id
   *
   * New browser session:
   * - Creates a new session_id
   */

  function getSessionId() {
    let sessionId = null;

    try {
      sessionId = sessionStorage.getItem(
        CONFIG.sessionStorageKey
      );

      if (!sessionId) {
        sessionId = createUUID();

        sessionStorage.setItem(
          CONFIG.sessionStorageKey,
          sessionId
        );

        debugLog(
          "New session_id created:",
          sessionId
        );
      }
    } catch (error) {
      sessionId = createUUID();

      debugLog(
        "sessionStorage unavailable. Temporary session_id:",
        sessionId
      );
    }

    return sessionId;
  }

  /* ============================================================
     06. SAFE PAGE URL
  ============================================================ */

  /**
   * Returns the page URL without:
   *
   * - Query parameters
   * - URL fragments
   *
   * Example:
   *
   * Input:
   * https://myvelvettouch.com/pages/test?email=test@example.com
   *
   * Output:
   * https://myvelvettouch.com/pages/test
   *
   * Security purpose:
   *
   * Reduce accidental collection of
   * personal information or tokens
   * included in URL parameters.
   */

  function getSafePageUrl() {
    return (
      window.location.origin +
      window.location.pathname
    );
  }

  /* ============================================================
     07. SAFE REFERRER
  ============================================================ */

  /**
   * Returns only the referrer origin.
   *
   * Example:
   *
   * Input:
   * https://example.com/page?token=123456
   *
   * Output:
   * https://example.com
   *
   * Security purpose:
   *
   * Prevent accidental transmission
   * of sensitive referrer URL parameters.
   */

  function getSafeReferrer() {
    try {
      if (!document.referrer) {
        return null;
      }

      const referrerUrl = new URL(
        document.referrer
      );

      if (
        referrerUrl.protocol !== "https:" &&
        referrerUrl.protocol !== "http:"
      ) {
        return null;
      }

      return referrerUrl.origin;

    } catch (error) {
      return null;
    }
  }

  /* ============================================================
     08. EVENT COLLECTION
  ============================================================ */

  /**
   * Send event to Cloud Run.
   *
   * Existing payload structure is preserved
   * for compatibility with the collector.
   */

  async function sendEvent(eventName, data = {}) {
    const payload = {
      event_id: createUUID(),

      anonymous_id: getAnonymousId(),

      session_id: getSessionId(),

      event_name: eventName,

      // Sanitized URL.
      page_url: getSafePageUrl(),

      page_path: window.location.pathname,

      page_title: document.title,

      object_type:
        data.object_type || "page",

      object_id:
        data.object_id ||
        window.location.pathname,

      event_properties:
        data.event_properties || {}
    };

    debugLog(
      "Sending event:",
      payload
    );

    try {
      const response = await fetch(
        CONFIG.endpoint,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json"
          },

          body: JSON.stringify(payload),

          keepalive: true
        }
      );

      const responseText =
        await response.text();

      if (!response.ok) {
        console.error(
          "[MVT Tracker] Collector error:",
          response.status
        );

        return;
      }

      debugLog(
        "Event accepted:",
        responseText
      );

    } catch (error) {
      console.error(
        "[MVT Tracker] Network error:",
        error
      );
    }
  }

  /* ============================================================
     09. PAGE VIEW TRACKING
  ============================================================ */

  /**
   * Automatically records page_view.
   *
   * Referrer is sanitized before sending.
   */

  function trackPageView() {
    sendEvent(
      "page_view",
      {
        object_type: "page",

        object_id:
          window.location.pathname,

        event_properties: {
          referrer: getSafeReferrer()
        }
      }
    );
  }

  /* ============================================================
     10. INITIALIZATION
  ============================================================ */

  function initialize() {
    const anonymousId = getAnonymousId();

    const sessionId = getSessionId();

    debugLog(
      "Tracker initialized"
    );

    debugLog(
      "anonymous_id:",
      anonymousId
    );

    debugLog(
      "session_id:",
      sessionId
    );

    trackPageView();
  }

  /* ============================================================
     11. DOM READY
  ============================================================ */

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

  /* ============================================================
     12. PUBLIC API
  ============================================================ */

  /**
   * Preserve the existing public API
   * for future integrations.
   */

  window.MVTTracker = {
    getAnonymousId,
    getSessionId,
    sendEvent
  };

})();